import { useEffect, useMemo, useState } from 'react';
import { isWithinDateRange } from '@practium/shared';
import type { FilterGroup } from '@practium/ui';
import {
	MIXTURE_CUSTOM_DAY_FILTER,
	MIXTURE_PAGE_SIZE,
	MIXTURE_PENDING_LABEL,
	MIXTURE_STATUS_LABELS,
} from '../constants/mixtures.constants';
import type { MaintenanceMixture } from '../types';

interface UseMixtureListFilterOptions {
	mixtures: MaintenanceMixture[];
	showSector: boolean;
	month: string;
}

const EMPTY_RANGE = { from: '', to: '' };

const valuesOf = (filters: string[], prefix: string) =>
	filters
		.filter((value) => value.startsWith(`${prefix}:`))
		.map((value) => value.slice(prefix.length + 1));

const mixtureDay = (mixture: MaintenanceMixture) => mixture.date.slice(0, 10);

const buildFilterGroups = (mixtures: MaintenanceMixture[], showSector: boolean): FilterGroup[] => [
	{
		label: 'Período',
		options: [
			{ value: MIXTURE_CUSTOM_DAY_FILTER, label: 'Selecionar dias do mês', hideCustomAction: true },
		],
	},
	{
		label: 'Situação',
		options: [
			{ value: 'status:pending', label: MIXTURE_PENDING_LABEL },
			{ value: 'status:confirmed', label: MIXTURE_STATUS_LABELS.confirmed },
		],
	},
	...(showSector
		? [
				{
					label: 'Setor',
					options: [
						...new Map(
							mixtures.map((mixture) => [mixture.sectorId, mixture.sectorName ?? mixture.sectorId])
						),
					].map(([sectorId, sectorName]) => ({ value: `sector:${sectorId}`, label: sectorName })),
				},
			]
		: []),
];

export const useMixtureListFilter = ({
	mixtures,
	showSector,
	month,
}: UseMixtureListFilterOptions) => {
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [range, setRange] = useState(EMPTY_RANGE);
	const [page, setPage] = useState(1);

	useEffect(() => {
		setFilterValues((current) => current.filter((value) => value !== MIXTURE_CUSTOM_DAY_FILTER));
		setRange(EMPTY_RANGE);
	}, [month]);

	useEffect(() => {
		setPage(1);
	}, [filterValues, range, month]);

	const filterGroups = useMemo(
		() => buildFilterGroups(mixtures, showSector),
		[mixtures, showSector]
	);
	const eventDays = useMemo(() => [...new Set(mixtures.map(mixtureDay))], [mixtures]);

	const criteria = useMemo(
		() => ({
			statuses: valuesOf(filterValues, 'status'),
			sectors: valuesOf(filterValues, 'sector'),
			byRange: filterValues.includes(MIXTURE_CUSTOM_DAY_FILTER) && !!range.from,
		}),
		[filterValues, range.from]
	);
	const isFiltered =
		criteria.statuses.length > 0 || criteria.sectors.length > 0 || criteria.byRange;

	const filteredMixtures = useMemo(
		() =>
			mixtures.filter(
				(mixture) =>
					(!criteria.statuses.length || criteria.statuses.includes(mixture.status)) &&
					(!criteria.sectors.length || criteria.sectors.includes(mixture.sectorId)) &&
					(!criteria.byRange ||
						isWithinDateRange(mixtureDay(mixture), range.from, range.to || range.from))
			),
		[criteria, mixtures, range]
	);

	const confirmFilter = {
		...(criteria.byRange ? { dateFrom: range.from, dateTo: range.to || range.from } : {}),
		...(criteria.sectors.length ? { sectorIds: criteria.sectors } : {}),
	};

	const totalPages = Math.max(1, Math.ceil(filteredMixtures.length / MIXTURE_PAGE_SIZE));
	const activePage = Math.min(page, totalPages);
	const visibleMixtures = filteredMixtures.slice(
		(activePage - 1) * MIXTURE_PAGE_SIZE,
		activePage * MIXTURE_PAGE_SIZE
	);

	const setDateRange = (from: string, to: string) => setRange({ from, to });

	return {
		month,
		eventDays,
		filterValues,
		setFilterValues,
		filterGroups,
		dateFrom: range.from,
		dateTo: range.to,
		setDateRange,
		page: activePage,
		totalPages,
		setPage,
		visibleMixtures,
		isFiltered,
		filteredMixtures,
		confirmFilter,
		isFilteredEmpty: mixtures.length > 0 && filteredMixtures.length === 0,
	};
};
